import DateRangeIcon from "@mui/icons-material/DateRange";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PublicIcon from "@mui/icons-material/Public";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Button,
  ButtonGroup,
  Paper,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";

import { RobotoTooltip, RobotoTypography } from "@/shared/components";
import type { DateRange, QuickFilterOption } from "@/shared/components";
import { usePopupState } from "@/shared/hooks/usePopupState";

import { DEFAULT_QUICK_FILTERS } from "./constants";
import { useQuickFilters } from "./useQuickFilters";

type TimezoneMode = "local" | "utc";

interface DateRangeSelectorProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  quickFilters?: QuickFilterOption[];
  defaultQuickFilter?: QuickFilterOption;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  dateRange,
  onDateRangeChange,
  quickFilters = DEFAULT_QUICK_FILTERS,
  defaultQuickFilter,
}) => {
  const theme = useTheme();
  const popupState = usePopupState();
  const [timezoneMode, setTimezoneMode] = React.useState<TimezoneMode>("local");

  const currentTimezone = dayjs.tz.guess();

  const { setQuickTimeRange, isQuickFilterActive, activeQuickFilter } =
    useQuickFilters({
      dateRange,
      onDateRangeChange,
      quickFilters,
      defaultQuickFilter,
    });

  // Handle timezone mode changes
  const handleTimezoneChange = React.useCallback(
    (_: React.MouseEvent<HTMLElement>, newTimezone: TimezoneMode | null) => {
      if (newTimezone !== null) {
        setTimezoneMode(newTimezone);

        // Convert existing dates to the new timezone if they exist
        if (dateRange.startDate && dateRange.endDate) {
          const convertedStart =
            newTimezone === "utc"
              ? dateRange.startDate.utc()
              : dateRange.startDate.tz(currentTimezone);
          const convertedEnd =
            newTimezone === "utc"
              ? dateRange.endDate.utc()
              : dateRange.endDate.tz(currentTimezone);

          onDateRangeChange({
            startDate: convertedStart,
            endDate: convertedEnd,
          });
        }
      }
    },
    [
      dateRange.startDate,
      dateRange.endDate,
      currentTimezone,
      onDateRangeChange,
    ],
  );

  // Handle date changes
  const handleStartDateChange = React.useCallback(
    (newValue: Dayjs | null) => {
      onDateRangeChange({
        startDate: newValue,
        endDate: dateRange.endDate,
      });
    },
    [dateRange.endDate, onDateRangeChange],
  );

  const handleEndDateChange = React.useCallback(
    (newValue: Dayjs | null) => {
      onDateRangeChange({
        startDate: dateRange.startDate,
        endDate: newValue,
      });
    },
    [dateRange.startDate, onDateRangeChange],
  );

  const hasCustomDateRange =
    dateRange.startDate && dateRange.endDate && !activeQuickFilter;

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
      <RobotoTooltip title="Date Range">
        <DateRangeIcon sx={{ fontSize: "1.25rem" }} />
      </RobotoTooltip>

      <ButtonGroup size="small" variant="outlined">
        {/* Quick filter buttons */}
        {quickFilters.map((filter, index) => {
          const isActive = isQuickFilterActive(filter);
          return (
            <Button
              key={index}
              variant={isActive ? "contained" : "outlined"}
              onClick={() => setQuickTimeRange(filter.amount, filter.unit)}
              sx={{
                textTransform: "none",
                ...(isActive && {
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main,
                  },
                }),
                ...(!isActive && {
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: "transparent",
                  },
                }),
              }}
            >
              {filter.label}
            </Button>
          );
        })}

        {/* Custom range button */}
        <Button
          variant={hasCustomDateRange ? "contained" : "outlined"}
          color="primary"
          endIcon={<ExpandMoreIcon />}
          onClick={(event) =>
            popupState.open({ anchorEl: event.currentTarget })
          }
          sx={{
            textTransform: "none",
            ...(hasCustomDateRange && { boxShadow: "none" }),
            ...(!hasCustomDateRange && {
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor: "transparent",
              },
            }),
          }}
        >
          Custom
        </Button>
      </ButtonGroup>

      {/* Custom range dropdown */}
      <Popover
        anchorEl={popupState.anchor}
        open={popupState.isOpen}
        onClose={popupState.close}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        marginThreshold={5}
      >
        <Paper
          elevation={8}
          sx={{
            minWidth: 400,
            padding: theme.spacing(2),
          }}
        >
          <Stack spacing={2}>
            <RobotoTypography variant="subtitle2">
              Custom Date Range
            </RobotoTypography>

            {/* Date Pickers */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <DateTimePicker
                label="Start"
                value={dateRange.startDate}
                onChange={handleStartDateChange}
                timezone={timezoneMode === "utc" ? "UTC" : currentTimezone}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { minWidth: 180 },
                  },
                }}
              />

              <DateTimePicker
                label="End"
                value={dateRange.endDate}
                onChange={handleEndDateChange}
                timezone={timezoneMode === "utc" ? "UTC" : currentTimezone}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { minWidth: 180 },
                  },
                }}
              />
            </Stack>

            {/* Timezone Selection */}
            <Stack direction="row" spacing={1} alignItems="center">
              <ToggleButtonGroup
                value={timezoneMode}
                exclusive
                onChange={handleTimezoneChange}
                size="small"
                sx={{ height: "32px" }}
              >
                <ToggleButton value="local" sx={{ px: 1.5 }}>
                  <ScheduleIcon sx={{ mr: 0.5, fontSize: "1rem" }} />
                  Local ({currentTimezone})
                </ToggleButton>
                <ToggleButton value="utc" sx={{ px: 1.5 }}>
                  <PublicIcon sx={{ mr: 0.5, fontSize: "1rem" }} />
                  UTC
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
        </Paper>
      </Popover>
    </Stack>
  );
};
